import { capitalize } from '../utils/capitalize'
import { addToCart } from './add-to-cart'
import { formatMoney } from './g-utils'

class ProductSection extends HTMLElement {
  constructor() {
    super()

    this.productForm = this.querySelector('.js-product-form')
    this.optionRadios = this.productForm.querySelectorAll('.js-option-radio')
  }

  connectedCallback() {
    this._initStickyAside()
    this._attachEventListeners()
    this._observeDeliverySection()
  }

  _initStickyAside() {
    const header = document.querySelector('#main-header .js-header')

    if (header) {
      let topGap = header.offsetHeight + 16
      this.style.setProperty('--top-gap', topGap + `px`)
    }
  }

  _attachEventListeners() {
    this.productForm.addEventListener('submit', this._onFormSubmit.bind(this))
    this.optionRadios.forEach((radio) => {
      radio.addEventListener('change', this._onOptionChange.bind(this))
    })
  }

  _onOptionChange(e) {
    const optionIndex = +e.target.dataset.optionPosition - 1
    const newOptions = [...this.selectedVariant.options]
    newOptions[optionIndex] = e.target.value

    for (let [id, variant] of Object.entries(this.productJson.variants)) {
      if (variant.options.join(',') === newOptions.join(',')) {
        this.selectedVariantId = id
        this._variantDeliveryInfo = null
        this._getDynamicDeliveryInfo()
        return
      }
    }
  }

  async _onFormSubmit(e) {
    e.preventDefault()

    await addToCart(
      [
        {
          id: this.selectedVariantId,
          quantity: this.productForm['quantity'].value,
          properties: {
            Auteur: this.productForm['properties[Auteur]'].value,
            _collectionTitle: this.productForm['properties[_collectionTitle]'].value,
            _collectionId: this.productForm['properties[_collectionId]'].value,
            _addedFrom: 'product_section'
          }
        }
      ],
      {
        klaviyoItems: [this.klaviyoAtcEventData],
        eventData: this.atcEventData
      }
    )
  }

  get selectedVariantId() {
    return this.productForm['variant-id'].value
  }

  set selectedVariantId(value) {
    this.productForm['variant-id'].value = value
    this.updateUrl()
    this.updatePrices()
    this.updateLowInStock()
    this.updateDeliveryInfo()
    document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: this.selectedVariant } }))
  }

  updateUrl() {
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.selectedVariantId)
    window.history.replaceState(null, '', url.toString())
  }

  updatePrices() {
    if (this.selectedVariant.available) {
      this.priceBlockEls.forEach((el) => el.setAttribute('aria-hidden', false))
      this.atcBtnEl.setAttribute('aria-hidden', false)
      this.bisTriggerContainer.setAttribute('aria-hidden', true)
      this.oosMessageContainer.setAttribute('aria-hidden', true)

      const price = this.selectedVariant.price
      const compareAtPrice = this.selectedVariant.compare_at_price

      this.priceEls.forEach((el) => (el.innerHTML = formatMoney(price)))
      if (compareAtPrice > price) {
        this.compareAtPriceBlockEls.forEach((el) => el.setAttribute('aria-hidden', false))
        this.compareAtPriceEls.forEach((el) => (el.innerHTML = formatMoney(compareAtPrice)))
        this.discountPercentageEls.forEach(
          (el) => (el.innerHTML = Math.round(((compareAtPrice - price) / compareAtPrice) * 100))
        )
      } else {
        this.compareAtPriceBlockEls.forEach((el) => el.setAttribute('aria-hidden', true))
      }
    } else {
      this.priceBlockEls.forEach((el) => el.setAttribute('aria-hidden', true))
      this.atcBtnEl.setAttribute('aria-hidden', true)
      this.bisTriggerContainer.setAttribute('aria-hidden', false)
      this.oosMessageContainer.setAttribute('aria-hidden', false)
    }
  }

  updateLowInStock() {
    this.visibleLowInStockEl?.setAttribute('aria-hidden', true)
    this.getLowInStockEl(this.selectedVariantId)?.setAttribute('aria-hidden', false)
  }

  async updateDeliveryInfo() {
    if (this.selectedVariant.is_new) {
      this.deliveryInfoNewEls.forEach((el) => el.setAttribute('aria-hidden', false))
      this.deliveryInfoUsedEls.forEach((el) => el.setAttribute('aria-hidden', true))
    } else {
      this.deliveryInfoNewEls.forEach((el) => el.setAttribute('aria-hidden', true))
      this.deliveryInfoUsedEls.forEach((el) => el.setAttribute('aria-hidden', false))
    }
  }

  _addDays(days) {
    return new Date(Date.now() + days * 24 * 60 * 60 * 1000)
  }

  _formatDate(date, locale) {
    return date.toLocaleDateString(locale.replace('_', '-'), {
      day: '2-digit',
      month: 'long'
    })
  }

  _hydrateDeliveryReassurance() {
    const bestDeliveryMethod = this._variantDeliveryInfo?.best_delivery_method
    const vendor = this._variantDeliveryInfo?.vendor
    const trend = this._variantDeliveryInfo?.trend?.toLowerCase()

    if (bestDeliveryMethod && vendor) {
      this.deliveryInfoFallback.classList.add('jc-hidden')
      this.deliveryInfoDynamic.classList.remove('jc-hidden')

      // Sans prix exploitables, on garde la date mais on neutralise l'ouverture
      // du modal de transporteurs (et ses chevrons) qui serait vide.
      const hasPrices = this._hasDeliveryMethodPrices()
      this._deliveryModalDisabled = !hasPrices
      this.deliveryModalChevrons.forEach((chevron) => {
        chevron.style.display = hasPrices ? '' : 'none'
      })

      this.deliveryVendors.forEach((vendorEl) =>
        vendorEl.getAttribute('data-vendor') === vendor
          ? vendorEl.classList.remove('jc-hidden')
          : vendorEl.classList.add('jc-hidden')
      )

      this.deliveryTrends.forEach((trendEl) =>
        trendEl.getAttribute('data-trend') === trend
          ? trendEl.classList.remove('jc-hidden')
          : trendEl.classList.add('jc-hidden')
      )

      this.deliveryTrendTooltips.forEach((trendTooltipEl) =>
        trendTooltipEl.getAttribute('data-trend') === trend
          ? trendTooltipEl.classList.remove('jc-hidden')
          : trendTooltipEl.classList.add('jc-hidden')
      )

      // estimated_delivery_min et estimated_delivery_max sont en jours
      const { estimated_delivery_min: estMinDays = 0, estimated_delivery_max: estMaxDays = 0 } =
        bestDeliveryMethod || {}

      const minDate = this._addDays(estMinDays)
      const maxDate = this._addDays(estMaxDays)

      const [minDay, minMonth] = this._formatDate(minDate, this._language).split(' ')
      const [maxDay, maxMonth] = this._formatDate(maxDate, this._language).split(' ')

      const deliveryDateString =
        minMonth === maxMonth
          ? `${minDay} - ${maxDay} ${capitalize(maxMonth)}`
          : `${minDay} ${capitalize(minMonth)} - ${maxDay} ${capitalize(maxMonth)}`

      this.deliveryDate.innerHTML = deliveryDateString

      this.deliveryTooltipTrigger.addEventListener('mouseenter', this._showTooltip.bind(this))
      this.deliveryTooltipTrigger.addEventListener('mouseleave', this._hideTooltip.bind(this))
    } else {
      this.deliveryInfoDynamic.classList.add('jc-hidden')
      this.deliveryInfoFallback.classList.remove('jc-hidden')
    }
  }

  _observeDeliverySection() {
    const target = this.querySelector('.js-delivery-info')
    if (!target) return

    // Bloque l'ouverture du modal (clic) quand il n'y a pas de prix, sans
    // toucher au hover du tooltip. Capture sur l'ancêtre → précède le listener
    // du <modal-trigger>.
    target.addEventListener(
      'click',
      (e) => {
        if (this._deliveryModalDisabled && e.target.closest('modal-trigger')) e.stopPropagation()
      },
      true
    )

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target)
          this._getAllVariantsDeliveryInfo()
        }
      })
    })

    observer.observe(target)
  }

  async _getAllVariantsDeliveryInfo() {
    try {
      this._country = this.deliveryInfoDynamic.getAttribute('data-country')
      this._language = this.deliveryInfoDynamic.getAttribute('data-language')
      const apiEndpoint = import.meta.env.PUBLIC_DELIVERY_API_ENDPOINT

      if (!apiEndpoint) {
        throw new Error(`PUBLIC_DELIVERY_API_ENDPOINT is not defined`)
      }

      if (!this._country || !this._language) {
        throw new Error(`Missing country or language attributes for delivery info`)
      }

      const url = new URL(apiEndpoint)
      url.searchParams.set('country', this._country)
      url.searchParams.set('language', this._language)
      url.searchParams.set('variantIds', `${this.productVariants.join(',')}`)

      const variantsDeliveryResponse = await fetch(url.toString())

      if (!variantsDeliveryResponse.ok) {
        throw new Error(`HTTP error! status: ${variantsDeliveryResponse.status}`)
      }

      const variantsDelivery = await variantsDeliveryResponse.json()

      this._variantsDelivery = variantsDelivery.data
      window.deliveryInfoCache = variantsDelivery.data

      this._getDynamicDeliveryInfo()
    } catch (error) {
      this.deliveryInfoDynamic.classList.add('jc-hidden')
      this.deliveryInfoFallback.classList.remove('jc-hidden')

      console.error('Error during fetching product delivery information', error)
    }
  }

  async _getDynamicDeliveryInfo() {
    if (!this._variantsDelivery) {
      console.warn('Missing variants information')
    }

    this._variantDeliveryInfo = this._variantsDelivery.find((variant) => variant.variant_id === this.selectedVariantId)

    this._hydrateDeliveryReassurance()
  }

  _showTooltip() {
    this.deliveryTooltipWrapper.style.opacity = 1
  }

  _hideTooltip() {
    this.deliveryTooltipWrapper.style.opacity = 0
  }

  _hasDeliveryMethodPrices() {
    const variants = this._variantsDelivery || window.deliveryInfoCache || []
    return variants.some((variant) =>
      variant?.delivery_methods?.some(
        (method) => !!method?.new_prices?.length || !!method?.second_hand_prices?.length
      )
    )
  }

  get deliveryInfoFallback() {
    if (!this._deliveryInfoFallback) {
      this._deliveryInfoFallback = this.querySelector('.js-delivery-info-fallback')
    }
    return this._deliveryInfoFallback
  }
  get deliveryInfoDynamic() {
    if (!this._deliveryInfoDynamic) {
      this._deliveryInfoDynamic = this.querySelector('.js-delivery-info-dynamic')
    }
    return this._deliveryInfoDynamic
  }
  get deliveryVendors() {
    if (!this._deliveryVendors) {
      this._deliveryVendors = Array.from(this.querySelectorAll('.js-delivery-vendor'))
    }
    return this._deliveryVendors
  }
  get deliveryTooltipTrigger() {
    if (!this._deliveryTooltipTrigger) {
      this._deliveryTooltipTrigger = this.querySelector('.js-tooltip-trigger')
    }
    return this._deliveryTooltipTrigger
  }
  get deliveryTooltipWrapper() {
    if (!this._deliveryTooltipWrapper) {
      this._deliveryTooltipWrapper = this.querySelector('.js-tooltip-wrapper')
    }
    return this._deliveryTooltipWrapper
  }
  get deliveryTrendTooltips() {
    if (!this._deliveryTrendTooltips) {
      this._deliveryTrendTooltips = Array.from(this.querySelectorAll('.js-delivery-trend-tooltip'))
    }
    return this._deliveryTrendTooltips
  }
  get deliveryTrends() {
    if (!this._deliveryTrends) {
      this._deliveryTrends = Array.from(this.querySelectorAll('.js-delivery-trend'))
    }
    return this._deliveryTrends
  }
  get deliveryModalChevrons() {
    if (!this._deliveryModalChevrons) {
      this._deliveryModalChevrons = Array.from(this.querySelectorAll('.js-delivery-modal-chevron'))
    }
    return this._deliveryModalChevrons
  }
  get deliveryDate() {
    if (!this._deliveryDate) {
      this._deliveryDate = this.querySelector('.js-delivery-date')
    }
    return this._deliveryDate
  }
  get deliveryInfoEl() {
    if (!this._deliveryInfoEl) {
      this._deliveryInfoEl = this.querySelector('.js-delivery-info')
    }
    return this._deliveryInfoEl
  }
  get deliveryInfoNewEls() {
    if (!this._deliveryInfoNewEls) {
      this._deliveryInfoNewEls = this.deliveryInfoEl.querySelectorAll('.js-new')
    }
    return this._deliveryInfoNewEls
  }

  get deliveryInfoUsedEls() {
    if (!this._deliveryInfoUsedEls) {
      this._deliveryInfoUsedEls = this.deliveryInfoEl.querySelectorAll('.js-used')
    }
    return this._deliveryInfoUsedEls
  }

  get visibleLowInStockEl() {
    if (!this._lowInStockEls) {
      this._lowInStockEls = Array.from(this.querySelectorAll('[data-low-in-stock]'))
    }

    return this._lowInStockEls.find((el) => el.getAttribute('aria-hidden') === 'false')
  }

  getLowInStockEl(variantId) {
    if (!this._lowInStockEls) {
      this._lowInStockEls = Array.from(this.querySelectorAll('[data-low-in-stock]'))
    }

    return this._lowInStockEls.find((el) => el.dataset.lowInStock == variantId)
  }

  get bisTriggerContainer() {
    if (!this._bisTriggerContainer) {
      this._bisTriggerContainer = this.querySelector('.js-bis-trigger-container')
    }
    return this._bisTriggerContainer
  }

  get oosMessageContainer() {
    if (!this._oosMessageContainer) {
      this._oosMessageContainer = this.querySelector('.js-oos-message-container')
    }
    return this._oosMessageContainer
  }

  get atcBtnEl() {
    if (!this._atcBtnEl) {
      this._atcBtnEl = this.querySelector('.js-atc-btn')
    }
    return this._atcBtnEl
  }

  get priceBlockEls() {
    if (!this._priceBlockEls) {
      this._priceBlockEls = this.querySelectorAll('.js-price-block')
    }
    return this._priceBlockEls
  }

  get priceEls() {
    if (!this._priceEls) {
      this._priceEls = this.querySelectorAll('.js-price')
    }
    return this._priceEls
  }

  get compareAtPriceBlockEls() {
    if (!this._compareAtPriceBlockEls) {
      this._compareAtPriceBlockEls = this.querySelectorAll('.js-price-block .js-compare-at-price-block')
    }
    return this._compareAtPriceBlockEls
  }

  get compareAtPriceEls() {
    if (!this._compareAtPriceEls) {
      this._compareAtPriceEls = this.querySelectorAll('.js-price-block .js-compare-at-price-block .js-compare-at-price')
    }
    return this._compareAtPriceEls
  }

  get discountPercentageEls() {
    if (!this._discountPercentageEls) {
      this._discountPercentageEls = this.querySelectorAll(
        '.js-price-block .js-compare-at-price-block .js-discount-percentage'
      )
    }
    return this._discountPercentageEls
  }

  get selectedVariant() {
    return this.productJson.variants[this.selectedVariantId]
  }

  get productVariants() {
    return Object.keys(this.productJson.variants)
  }

  get atcEventData() {
    if (!this._initialAtcData) {
      this._initialAtcData = JSON.parse(this.querySelector('.js-atc-event-data').innerHTML)
    }
    const newAtcEventData = { ...this._initialAtcData }
    newAtcEventData.cloud_retail.visitorId = localStorage.getItem('visitorId')
    newAtcEventData.cloud_retail.productDetails = [
      {
        product: {
          id: this.selectedVariant.barcode
        },
        quantity: 1
      }
    ]
    newAtcEventData.cloud_retail.cartId = window.cartToken
    if (window.userId && window.userId !== '') {
      newAtcEventData.cloud_retail.userInfo = { userId: window.userId }
    }
    newAtcEventData.productDetails[0].product.id = this.selectedVariant.barcode

    return newAtcEventData
  }

  get klaviyoAtcEventData() {
    if (!this._initialKlaviyoAtcEventData) {
      this._initialKlaviyoAtcEventData = JSON.parse(this.querySelector('.js-klaviyo-atc-event-data').innerHTML)
    }

    const _klaviyoAtcEventData = { ...this._initialKlaviyoAtcEventData }
    _klaviyoAtcEventData.Price = formatMoney(this.selectedVariant.price)

    return _klaviyoAtcEventData
  }

  get productJson() {
    if (!this._productJson) {
      this._productJson = JSON.parse(this.querySelector('.js-product-json').innerHTML)
    }
    return this._productJson
  }

  get settings() {
    if (!this._settings) {
      this._settings = JSON.parse(this.dataset.sectionSettings)
    }
    return this._settings
  }
}

customElements.define('product-section', ProductSection)
